import { Close as CloseIcon } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  LinearProgress,
  Paper,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  submitRespuestas,
  verificarTiempoEvaluacion,
  iniciarEvaluacion,
  guardarBorrador,
  obtenerBorrador,
  type FormularioPendiente,
  type RespuestaItemDto,
} from '../../api/formularios';

import FormularioRespuestaDialog from './FormularioRespuestaDialog';
import ConfirmExitEvaluacionDialog from './ConfirmExitEvaluacionDialog';

interface Props {
  open: boolean;
  formulario: FormularioPendiente & { tiempoLimite?: number };
  onClose: () => void;
  onComplete: () => void;
  autoCloseOnResult?: boolean;
}

interface TiempoEstado {
  minutosRestantes: number;
  segundosRestantes: number;
  porcentaje: number;
  expirada: boolean;
}

export default function EvaluacionConTiempoDialog({
  open,
  formulario,
  onClose,
  onComplete,
  autoCloseOnResult,
}: Props) {
  const [tiempoEstado, setTiempoEstado] = useState<TiempoEstado | null>(null);
  const [iniciando, setIniciando] = useState(false);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(true);
  const [mostrarEvaluacion, setMostrarEvaluacion] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [evaluacionIniciada, setEvaluacionIniciada] = useState(false);
  const [respuestas, setRespuestas] = useState<RespuestaItemDto[]>([]);
  const [showConfirmAutoSubmit, setShowConfirmAutoSubmit] = useState(false);
  const [resultadoEvaluacion, setResultadoEvaluacion] = useState<{
    calificacion: number;
    aprobado: boolean;
    competenciasFalladas?: string[];
  } | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation<'translation'>();

  // Mantener siempre la última versión de las respuestas sin recrear callbacks
  const respuestasRef = useRef<RespuestaItemDto[]>([]);
  useEffect(() => {
    respuestasRef.current = respuestas;
  }, [respuestas]);

  const tiempoLimiteMinutos = formulario.tiempoLimite ?? null;

  const enviarEvaluacionAutomaticamente = useCallback(async () => {
    setCargando(true);
    try {
      // Guardar borrador final
      const respuestasActuales = respuestasRef.current || [];
      if (respuestasActuales.length > 0) {
        await guardarBorrador(formulario.id, { respuestas: respuestasActuales });
      }

      // Enviar respuestas
      const result = await submitRespuestas(formulario.id, { respuestas: respuestasActuales });

      if (result && typeof result === 'object' && result.calificacion !== undefined) {
        setResultadoEvaluacion({
          calificacion: Number(result.calificacion),
          aprobado: Boolean(result.aprobado),
          competenciasFalladas: result.feedback?.competenciasFalladas || [],
        });
      } else {
        enqueueSnackbar('Evaluación enviada automáticamente (tiempo expirado)', {
          variant: 'info',
          autoHideDuration: 5000,
        });
        // Si no hay nota (caso raro), cerrar flujo normal
        onComplete();
        onClose();
        window.dispatchEvent(
          new CustomEvent('formulario:completed', {
            detail: { formularioId: formulario.id },
          }),
        );
      }
    } catch {
      enqueueSnackbar('Error al enviar evaluación automáticamente', {
        variant: 'error',
      });
    } finally {
      setCargando(false);
    }
  }, [formulario.id, enqueueSnackbar, onComplete, onClose]);

  const intervalRef = useRef<number | null>(null);

  const iniciarContador = useCallback(
    (minutosIniciales: number) => {
      // Limpiar cualquier intervalo previo
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      const totalSegundos = Math.max(1, Math.floor(Number(minutosIniciales))) * 60;
      let segundosRestantes = totalSegundos;

      const actualizarContador = () => {
        segundosRestantes--;

        if (segundosRestantes <= 0) {
          setTiempoEstado({
            minutosRestantes: 0,
            segundosRestantes: 0,
            porcentaje: 0,
            expirada: true,
          });

          enviarEvaluacionAutomaticamente();

          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          return;
        }

        const minutos = Math.floor(segundosRestantes / 60);
        const segundos = segundosRestantes % 60;
        const porcentaje = ((totalSegundos - segundosRestantes) / totalSegundos) * 100;

        setTiempoEstado({
          minutosRestantes: minutos,
          segundosRestantes: segundos,
          porcentaje,
          expirada: false,
        });
      };

      // Actualizar inmediatamente y arrancar intervalo
      actualizarContador();
      intervalRef.current = window.setInterval(actualizarContador, 1000) as unknown as number;
    },
    [enviarEvaluacionAutomaticamente],
  );

  useEffect(() => {
    // Verificar estado de la evaluación al abrir
    const verificarEstadoEvaluacion = async () => {
      try {
        const estado = await verificarTiempoEvaluacion(formulario.id);

        if (estado?.expirada) {
          enqueueSnackbar('El tiempo para esta evaluación ha expirado', { variant: 'warning' });
          onClose();
          return;
        }

        if (estado?.tiempoRestante !== undefined && estado.tiempoRestante !== null) {
          setEvaluacionIniciada(true);
          iniciarContador(estado.tiempoRestante);
          setMostrarConfirmacion(false);
          setMostrarEvaluacion(true);
        }
      } catch (error) {
        console.error('Error verificando estado de evaluación:', error);
      }
    };

    const cargarBorrador = async () => {
      try {
        const borrador = await obtenerBorrador(formulario.id);
        if (borrador?.respuestasJson?.respuestas) {
          setRespuestas(borrador.respuestasJson.respuestas);
        }
      } catch {
        // No hay borrador
      }
    };

    if (open) {
      verificarEstadoEvaluacion();
      cargarBorrador();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [open, formulario.id, iniciarContador, enqueueSnackbar, onClose]);

  // Evitar que el usuario cierre la pestaña durante la evaluación (antes deunload)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (evaluacionIniciada && !tiempoEstado?.expirada) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
      return undefined;
    };

    if (evaluacionIniciada) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [evaluacionIniciada, tiempoEstado]);

  // Guard para navegación interna (clic en enlaces y back/forward)
  useEffect(() => {
    const handleLinkClick = (e: MouseEvent) => {
      if (!evaluacionIniciada || tiempoEstado?.expirada) return;
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest && (target.closest('a') as HTMLAnchorElement | null);
      if (anchor && anchor.href && anchor.origin === window.location.origin) {
        const msg = String(
          t('evaluations.leaveConfirm', 'Salir cancelará la evaluación. ¿Continuar?'),
        );
        const ok = window.confirm(msg);
        if (!ok) e.preventDefault();
      }
    };

    const handlePopState = (_e: PopStateEvent) => {
      if (!evaluacionIniciada || tiempoEstado?.expirada) return;
      const msg = String(
        t('evaluations.leaveConfirm', 'Salir cancelará la evaluación. ¿Continuar?'),
      );
      const ok = window.confirm(msg);
      if (!ok) {
        // Revert the popstate navigation
        window.history.pushState(null, '', window.location.href);
      }
    };

    if (evaluacionIniciada) {
      document.addEventListener('click', handleLinkClick, true);
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      document.removeEventListener('click', handleLinkClick, true);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [evaluacionIniciada, tiempoEstado, t]);

  const handleIniciarEvaluacion = async () => {
    setIniciando(true);
    try {
      const respuesta = await iniciarEvaluacion(formulario.id);

      // Iniciar contador con fallback (API -> formulario -> verificarTiempo)
      const minsFromApi =
        typeof (respuesta as { tiempoLimite?: unknown })?.tiempoLimite === 'number'
          ? (respuesta as { tiempoLimite: number }).tiempoLimite
          : undefined;

      const mins = minsFromApi ?? tiempoLimiteMinutos ?? undefined;
      if (typeof mins === 'number') {
        iniciarContador(mins);
      } else {
        try {
          const estado = await verificarTiempoEvaluacion(formulario.id);
          if (estado?.tiempoRestante !== undefined && estado.tiempoRestante !== null) {
            iniciarContador(estado.tiempoRestante);
          }
        } catch {
          // best-effort
        }
      }

      setEvaluacionIniciada(true);
      setMostrarConfirmacion(false);
      setMostrarEvaluacion(true);

      const minsShown = minsFromApi ?? tiempoLimiteMinutos ?? undefined;
      enqueueSnackbar(
        `¡Evaluación iniciada!${typeof minsShown === 'number' ? ` Tienes ${minsShown} minutos para completarla.` : ''}`,
        {
          variant: 'info',
          autoHideDuration: 5000,
        },
      );
    } catch (error: unknown) {
      const axiosLike = error as {
        response?: {
          data?: {
            message?: string;
          };
        };
      };
      const message = axiosLike?.response?.data?.message;
      enqueueSnackbar(message || 'Error al iniciar evaluación', {
        variant: 'error',
      });
    } finally {
      setIniciando(false);
    }
  };

  const handleCerrarEvaluacion = () => {
    if (evaluacionIniciada && !tiempoEstado?.expirada) {
      setShowConfirmAutoSubmit(true);
      return;
    }

    onClose();
  };

  const handleRespuestasChange = (nuevasRespuestas: RespuestaItemDto[]) => {
    setRespuestas(nuevasRespuestas);
  };

  const handleCompletarEvaluacion = () => {
    onComplete();
    onClose();
  };

  const handleCerrarResultado = () => {
    setResultadoEvaluacion(null);
    onComplete();
    onClose();
  };

  // Mostrar siempre el resultado si existe (pantalla de nota)
  if (resultadoEvaluacion) {
    if (autoCloseOnResult) {
      const detalleCompetencias = resultadoEvaluacion.competenciasFalladas?.length
        ? ` Áreas: ${resultadoEvaluacion.competenciasFalladas.join(', ')}.`
        : '';
      enqueueSnackbar(
        `Evaluación enviada. Nota: ${resultadoEvaluacion.calificacion} (${resultadoEvaluacion.aprobado ? 'Aprobado' : 'No aprobado'}).${detalleCompetencias}`,
        { variant: resultadoEvaluacion.aprobado ? 'success' : 'warning' },
      );
      setResultadoEvaluacion(null);
      onComplete();
      onClose();
      window.dispatchEvent(
        new CustomEvent('formulario:completed', {
          detail: { formularioId: formulario.id },
        }),
      );
      return null;
    }
    return (
      <Dialog open={open} onClose={handleCerrarResultado} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Resultado de la evaluación</Typography>
        </DialogTitle>
        <DialogContent>
          <Box py={2}>
            <Alert severity={resultadoEvaluacion.aprobado ? 'success' : 'warning'} sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Nota: {resultadoEvaluacion.calificacion}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {resultadoEvaluacion.aprobado ? 'Aprobado' : 'No aprobado'}
              </Typography>
              {resultadoEvaluacion.competenciasFalladas &&
                resultadoEvaluacion.competenciasFalladas.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      Áreas de mejora:
                    </Typography>
                    <Typography variant="body2">
                      {resultadoEvaluacion.competenciasFalladas.join(', ')}
                    </Typography>
                  </Box>
                )}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCerrarResultado} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Diálogo de confirmación antes de iniciar
  if (mostrarConfirmacion && !evaluacionIniciada) {
    const minsInfo = typeof tiempoLimiteMinutos === 'number' ? tiempoLimiteMinutos : null;
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Evaluación con Tiempo Límite</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box py={2}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                ⚠️ Esta evaluación tiene un tiempo límite
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Una vez iniciada, tendrás {minsInfo ?? '—'} minutos para completarla.
              </Typography>
            </Alert>

            <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {formulario.titulo}
              </Typography>
              {formulario.descripcion && (
                <Typography variant="body2" color="text.secondary">
                  {formulario.descripcion}
                </Typography>
              )}
            </Paper>

            <Alert severity="info">
              <Typography variant="body2">
                <strong>Importante:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>El tiempo comenzará al hacer clic en "Iniciar Evaluación"</li>
                  <li>No podrás realizar otras acciones durante la evaluación</li>
                  <li>Si cierras la evaluación, se enviará automáticamente</li>
                  <li>Si se acaba el tiempo, se enviará automáticamente</li>
                </ul>
              </Typography>
            </Alert>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleIniciarEvaluacion}
            variant="contained"
            disabled={iniciando}
            startIcon={iniciando ? <CircularProgress size={20} /> : null}
          >
            {iniciando ? 'Iniciando...' : 'Iniciar Evaluación'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Barra de tiempo durante la evaluación
  const renderBarraTiempo = () => {
    if (tiempoEstado?.expirada) return null;

    // Importante: `FormularioRespuestaDialog` es un `Dialog` con portal (zIndex modal ~1300).
    // Para que el cronómetro sea visible, la barra debe ir en `position: fixed` y con zIndex mayor.
    if (!tiempoEstado) {
      return (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.modal + 1,
            bgcolor: 'background.paper',
          }}
        >
          <Paper elevation={3} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box flex={1} mr={2}>
                <LinearProgress />
              </Box>
              <Box sx={{ minWidth: 100, textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="bold">
                  --:--
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Cargando tiempo…
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      );
    }

    const color =
      tiempoEstado.porcentaje > 80 ? 'error' : tiempoEstado.porcentaje > 60 ? 'warning' : 'primary';

    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.modal + 1,
          bgcolor: 'background.paper',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 2,
            bgcolor: tiempoEstado.porcentaje > 80 ? 'error.lighter' : 'background.paper',
            borderTop: tiempoEstado.porcentaje > 80 ? 2 : 0,
            borderTopStyle: tiempoEstado.porcentaje > 80 ? 'solid' : 'none',
            borderTopColor: tiempoEstado.porcentaje > 80 ? 'error.main' : 'transparent',
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box flex={1} mr={2}>
              <LinearProgress
                variant="determinate"
                value={tiempoEstado.porcentaje}
                color={color}
                sx={{ height: 10, borderRadius: 5 }}
              />
            </Box>

            <Box sx={{ minWidth: 100, textAlign: 'center' }}>
              <Typography
                variant="h6"
                color={tiempoEstado.porcentaje > 80 ? 'error' : 'primary'}
                fontWeight="bold"
              >
                {String(tiempoEstado.minutosRestantes).padStart(2, '0')}:
                {String(tiempoEstado.segundosRestantes).padStart(2, '0')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tiempo restante
              </Typography>
            </Box>
          </Box>

          {tiempoEstado.porcentaje > 80 && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              ⚠️ ¡Queda poco tiempo! La evaluación se enviará automáticamente.
            </Typography>
          )}
        </Paper>
      </Box>
    );
  };

  // Evaluación en progreso
  if (mostrarEvaluacion && evaluacionIniciada) {
    return (
      <>
        {renderBarraTiempo()}

        <FormularioRespuestaDialog
          open={open}
          formulario={formulario}
          onClose={handleCerrarEvaluacion}
          onComplete={handleCompletarEvaluacion}
          onRespuestasChange={handleRespuestasChange}
          respuestasIniciales={respuestas}
          deshabilitarSalida={true}
          mostrarAdvertenciaTiempo={(tiempoEstado?.porcentaje ?? 0) > 80}
        />

        {cargando && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
          >
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6">Enviando evaluación...</Typography>
              <Typography variant="body2" color="text.secondary">
                Por favor, espera un momento
              </Typography>
            </Paper>
          </Box>
        )}

        <ConfirmExitEvaluacionDialog
          open={showConfirmAutoSubmit}
          onClose={() => setShowConfirmAutoSubmit(false)}
          onContinue={enviarEvaluacionAutomaticamente}
          loading={cargando}
        />
      </>
    );
  }

  return null;
}
